<template>
  <div
    class="relative mt-5 p-4 @sm:p-10 text-center"
    :style="{ backgroundColor: props.content.text?.bgColor ?? '#f5f5f5' }"
    data-testid="newsletter-block"
  >
    <TextContent v-bind="textContentProps" :test-id="'newsletter'" />

    <form class="mx-auto max-w-[550px] pt-2" novalidate @submit.prevent="onSubmit">
      <div
        v-if="props.content.input?.displayNameInput"
        class="grid grid-cols-1 @sm:grid-cols-2"
        data-testid="newsletter-display-name"
      >
        <div class="@sm:mr-[1rem]">
          <label for="newsletter-first-name">
            <UiFormLabel class="text-start">{{ t('newsletter.firstName') }}</UiFormLabel>
            <SfInput
              v-bind="firstNameAttributes"
              id="newsletter-first-name"
              v-model="firstName"
              :invalid="Boolean(errors['firstName'])"
              :placeholder="firstNameLabel.getPlaceholder('**').value"
              :wrapper-class="wrapperClass"
              type="text"
              name="firstName"
            />
          </label>
          <div class="h-[2rem]">
            <ErrorMessage as="div" name="firstName" class="text-negative-700 text-left text-sm pt-[0.2rem]" />
          </div>
        </div>

        <div class="@sm:ml-[1rem]">
          <label for="newsletter-last-name">
            <UiFormLabel class="text-start">{{ t('newsletter.lastName') }}</UiFormLabel>
            <SfInput
              v-bind="lastNameAttributes"
              id="newsletter-last-name"
              v-model="lastName"
              :invalid="Boolean(errors['lastName'])"
              :placeholder="lastNameLabel.getPlaceholder('**').value"
              :wrapper-class="wrapperClass"
              type="text"
              name="lastName"
            />
          </label>
          <div class="h-[2rem]">
            <ErrorMessage as="div" name="lastName" class="text-negative-700 text-left text-sm pt-[0.2rem]" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1">
        <label for="newsletter-email">
          <UiFormLabel class="text-start">{{ t('newsletter.email') }}</UiFormLabel>
          <SfInput
            v-bind="emailAttributes"
            id="newsletter-email"
            v-model="email"
            :invalid="Boolean(errors['email'])"
            :placeholder="`${t('newsletter.email')} **`"
            :wrapper-class="wrapperClass"
            type="email"
            name="email"
            autocomplete="email"
          />
        </label>
        <div class="h-[2rem]">
          <ErrorMessage as="div" name="email" class="text-negative-700 text-left text-sm pt-[0.2rem]" />
        </div>
      </div>

      <div class="text-base text-neutral-900">
        <div class="flex justify-center items-center">
          <SfCheckbox
            v-bind="privacyPolicyAttributes"
            id="terms-checkbox"
            v-model="privacyPolicy"
            :invalid="Boolean(errors['privacyPolicy'])"
            class="inline-block mr-2"
            data-testid="checkout-terms-checkbox"
          />
          <label for="terms-checkbox" class="text-left leading-5 select-none">
            <i18n-t keypath="newsletter.policy" scope="global">
              <template #privacyPolicy>
                <UiLink
                  :href="localePath(paths.privacyPolicy)"
                  target="_blank"
                  class="focus:outline focus:outline-offset-2 focus:outline-2 outline-secondary-600 rounded"
                >
                  {{ t('legal.privacyPolicy') }}
                </UiLink>
              </template>
            </i18n-t>
            **
          </label>
        </div>
        <div class="h-[2rem]">
          <ErrorMessage as="div" name="privacyPolicy" class="text-negative-700 text-left text-sm pt-[0.2rem]" />
        </div>
      </div>

      <div class="flex flex-col items-center">
        <UiButton type="submit" size="lg" :disabled="loading" data-testid="newsletter-button">
          <SfLoaderCircular v-if="loading" class="flex justify-center items-center" size="base" />
          <template v-else>{{ props.content.button?.label ?? t('newsletter.subscribe') }}</template>
        </UiButton>

        <NuxtTurnstile
          v-if="turnstileSiteKey.length > 0 && turnstileLoad"
          v-bind="turnstileAttributes"
          ref="turnstileElement"
          v-model="turnstile"
          :site-key="turnstileSiteKey"
          :options="{ theme: 'light' }"
          class="mt-4"
        />

        <ErrorMessage as="div" name="turnstile" class="text-negative-700 text-left text-sm pt-[0.2rem]" />
      </div>
    </form>

    <div class="text-left typography-text-xs mt-3">** {{ t('contact.form.asterixHint') }}</div>
  </div>
</template>

<script lang="ts" setup>
import { SfCheckbox, SfInput, SfLoaderCircular } from '@storefront-ui/vue';
import { useForm, ErrorMessage } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import { object, string, boolean } from 'yup';
import { paths } from '~/utils/paths';
import type { NewsletterSubscribeProps } from './types';

const { subscribe, loading } = useNewsletter();
const { send } = useNotification();
const localePath = useLocalePath();
const props = defineProps<NewsletterSubscribeProps>();
const { getSetting } = useSiteSettings('cloudflareTurnstileApiSiteKey');
const turnstileSiteKey = getSetting() ?? '';

const turnstileElement = ref();
const turnstileLoad = ref(false);
const wrapperClass = 'focus-within:outline focus-within:outline-offset';
const textContentProps = computed(() =>
  mapToTextContentProps({
    htmlDescription: props.content.text?.htmlDescription,
    textAlignment: props.content.text?.textAlignment ?? 'center',
  }),
);

const validationSchema = toTypedSchema(
  object({
    firstName: props.content.input?.nameIsRequired
      ? string().required(t('error.newsletter.firstNameRequired')).default('')
      : string().optional().default(''),
    lastName: props.content.input?.nameIsRequired
      ? string().required(t('error.newsletter.lastNameRequired')).default('')
      : string().optional().default(''),
    email: string().email(t('error.email.valid')).required(t('error.email.required')).default(''),
    privacyPolicy: boolean().oneOf([true], t('error.newsletter.termsRequired')).default(false),
    turnstile:
      turnstileSiteKey.length > 0
        ? string().required(t('error.newsletter.turnstileRequired')).default('')
        : string().optional().default(''),
  }),
);

const { errors, meta, defineField, handleSubmit, resetForm } = useForm({
  validationSchema: validationSchema,
});

const [firstName, firstNameAttributes] = defineField('firstName');
const [lastName, lastNameAttributes] = defineField('lastName');
const [email, emailAttributes] = defineField('email');
const [turnstile, turnstileAttributes] = defineField('turnstile');
const [privacyPolicy, privacyPolicyAttributes] = defineField('privacyPolicy');

const lastNameLabel = useFormLabel(
  t('newsletter.lastName'),
  computed(() => !props.content.input?.nameIsRequired),
);
const firstNameLabel = useFormLabel(
  t('newsletter.firstName'),
  computed(() => !props.content.input?.nameIsRequired),
);

const subscribeNewsletter = async () => {
  if (!meta.value.valid || !turnstile.value) {
    return;
  }

  const response = await subscribe({
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value || '',
    emailFolder: props.content.settings?.emailFolderId ?? 1,
    'cf-turnstile-response': turnstile.value,
  });

  if (response) {
    send({
      type: 'positive',
      message: t('newsletter.success'),
    });
    resetForm();
  }

  turnstile.value = '';
  turnstileElement.value?.reset();
};

const onSubmit = handleSubmit(() => subscribeNewsletter());

if (turnstileSiteKey.length > 0) {
  const turnstileWatcher = watch([firstName, lastName, email], (data) => {
    if (data.some((field) => field && field.length > 0)) {
      turnstileLoad.value = true;
      turnstileWatcher();
    }
  });
}
</script>
